import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import axiosInstance from "../../utils/axios";
import {
    Box,
    Heading,
    Text,
    Grid,
    GridItem,
    Button,
    useToast,
    Input,
    Textarea,
    VStack,
    HStack,
    Badge,
    Spinner,
    Table,
    Thead,
    Tbody,
    Tr,
    Th,
    Td,
    Accordion,
    AccordionItem,
    AccordionButton,
    AccordionPanel,
    AccordionIcon,
    FormControl,
    FormLabel,
} from "@chakra-ui/react";
import UserGroupsSection from './components/UserGroupsSection';

const ViewUser = () => {
    const { id } = useParams();
    const [user, setUser] = useState(null);
    const [commsLog, setCommsLog] = useState([]);
    const [newCommTitle, setNewCommTitle] = useState('');
    const [newCommBody, setNewCommBody] = useState('');
    const [isLoading, setIsLoading] = useState(true);
    const toast = useToast();

    const sectionStyle = {
        border: "1px solid",
        borderColor: "gray.200",
        borderRadius: "md",
        p: 4,
        mb: 6,
        boxShadow: "sm"
    };

    const headingStyle = {
        size: "md",
        mb: 4,
        fontWeight: "600"
    };

    useEffect(() => {
        const fetchUserData = async () => {
            try {
                const response = await axiosInstance.post(
                    `/dummy/get-user`,
                    { target_user_id: id }
                );

                if (response.data && response.data.data) {
                    const userData = response.data.data;
                    setUser(userData);
                    // Set the notification fields with user IDs
                    setNewCommTitle(`User ID: ${userData.id}`);
                    setNewCommBody(`WP User ID: ${userData.wp_user_id}`);
                    toast({
                        title: response.data.message,
                        description: response.data.description,
                        status: response.data.status,
                        duration: 3000,
                        isClosable: true,
                    });
                } else {
                    console.error('Invalid user data structure:', response.data);
                    toast({
                        title: "Error",
                        description: "Invalid user data received",
                        status: "error",
                        duration: 3000,
                        isClosable: true,
                    });
                }
            } catch (error) {
                console.error("Error fetching user:", error);
                toast({
                    title: "Error",
                    description: "Failed to fetch user data",
                    status: "error",
                    duration: 3000,
                    isClosable: true,
                });
            } finally {
                setIsLoading(false);
            }
        };

        const fetchCommsLog = async () => {
            try {
                const response = await axiosInstance.post(
                    `/dummy/get-comms-log`,
                    { target_user_id: id }
                );
                
                if (response.data && response.data.data) {
                    setCommsLog(response.data.data);
                }
            } catch (error) {
                console.error('Error fetching comms log:', error);
                toast({
                    title: 'Error',
                    description: 'Failed to fetch communications log',
                    status: 'error',
                    duration: 3000,
                    isClosable: true,
                });
            }
        };

        fetchUserData();
        fetchCommsLog();
    }, [id, toast]);

    const handleSendComm = async () => {
        try {
            const response = await axiosInstance.post(
                `/dummy/send-comm`,
                {
                    from_user_id: localStorage.getItem("user_id"),
                    to_user_id: user.id,
                    comms_type: "PUSH NOTIFICATION",
                    comms_category: "Admin Diginu",
                    msg_title: newCommTitle,
                    msg_body: newCommBody,
                    msg_data: "",
                }
            );

            toast({
                title: response.data.message,
                description: response.data.description,
                status: response.data.status,
                duration: 3000,
                isClosable: true,
            });
            
            const refreshedCommsLog = await axiosInstance.post(
                `/dummy/get-comms-log`,
                { target_user_id: user.id }
            );
            if (refreshedCommsLog.data && refreshedCommsLog.data.data) {
                setCommsLog(refreshedCommsLog.data.data);
            }
            setNewCommTitle("");
            setNewCommBody("");
        } catch (error) {
            console.error("Error sending communication:", error);
            toast({
                title: "Error",
                description: "Failed to send communication",
                status: "error",
                duration: 3000,
                isClosable: true,
            });
        }
    };

    const formatDate = (dateString) => {
        const date = new Date(dateString);
        const options = {
            month: "long",
            day: "numeric",
            hour: "2-digit",
            minute: "2-digit",
        };
        return date.toLocaleDateString("en-US", options);
    };

    if (isLoading) {
        return <Box p={4}>Loading...</Box>;
    }

    if (!user) {
        return <Box p={4}>User not found</Box>;
    }

    const userEntries = Object.entries(user);

    return (
        <Box p={4}>
            <Heading size="lg" mb={6}>User Details</Heading>
            
            {isLoading ? (
                <Box display="flex" justifyContent="center" alignItems="center" height="200px">
                    <Spinner size="xl" />
                </Box>
            ) : !user ? (
                <Text>User not found</Text>
            ) : (
                <Grid templateColumns="40% 60%" gap={6}>
                    {/* User Details Section - Left Column */}
                    <GridItem>
                        <Box borderWidth="1px" borderRadius="lg" p={4} shadow="sm">
                            <Heading size="md" mb={4}>User Information</Heading>
                            <Table variant="simple">
                                <Tbody>
                                    {Object.entries(user).map(([key, value]) => (
                                        <Tr key={key}>
                                            <Td fontWeight="bold">{key}</Td>
                                            <Td>{value?.toString() || ''}</Td>
                                        </Tr>
                                    ))}
                                </Tbody>
                            </Table>
                        </Box>
                    </GridItem>

                    {/* Right Column - Collapsible Sections */}
                    <GridItem>
                        <VStack spacing={4} align="stretch">
                            {/* Send Push Notification Section */}
                            <Box borderWidth="1px" borderRadius="lg" p={4} shadow="sm">
                                <Accordion allowToggle defaultIndex={[]}>
                                    <AccordionItem>
                                        <h2>
                                            <AccordionButton>
                                                <Box flex="1" textAlign="left">
                                                    <Heading size="md">Send Push Notification</Heading>
                                                </Box>
                                                <AccordionIcon />
                                            </AccordionButton>
                                        </h2>
                                        <AccordionPanel pb={4}>
                                            <VStack spacing={4} align="stretch">
                                                <FormControl>
                                                    <FormLabel>Title</FormLabel>
                                                    <Input
                                                        value={newCommTitle}
                                                        onChange={(e) => setNewCommTitle(e.target.value)}
                                                        placeholder="Enter notification title"
                                                    />
                                                </FormControl>
                                                <FormControl>
                                                    <FormLabel>Message</FormLabel>
                                                    <Textarea
                                                        value={newCommBody}
                                                        onChange={(e) => setNewCommBody(e.target.value)}
                                                        placeholder="Enter notification message"
                                                    />
                                                </FormControl>
                                                <Button
                                                    colorScheme="blue"
                                                    onClick={handleSendComm}
                                                    isLoading={isLoading}
                                                >
                                                    Send Notification
                                                </Button>
                                            </VStack>
                                        </AccordionPanel>
                                    </AccordionItem>
                                </Accordion>
                            </Box>

                            {/* User Groups Section */}
                            <Box borderWidth="1px" borderRadius="lg" p={4} shadow="sm">
                                <Accordion allowToggle defaultIndex={[]}>
                                    <AccordionItem>
                                        <h2>
                                            <AccordionButton>
                                                <Box flex="1" textAlign="left">
                                                    <Heading size="md">User Groups</Heading>
                                                </Box>
                                                <AccordionIcon />
                                            </AccordionButton>
                                        </h2>
                                        <AccordionPanel pb={4}>
                                            <UserGroupsSection userId={id} />
                                        </AccordionPanel>
                                    </AccordionItem>
                                </Accordion>
                            </Box>

                            {/* Communications Log Section */}
                            <Box borderWidth="1px" borderRadius="lg" p={4} shadow="sm">
                                <Accordion allowToggle defaultIndex={[]}>
                                    <AccordionItem>
                                        <h2>
                                            <AccordionButton>
                                                <Box flex="1" textAlign="left">
                                                    <Heading size="md">Communications Log</Heading>
                                                </Box>
                                                <AccordionIcon />
                                            </AccordionButton>
                                        </h2>
                                        <AccordionPanel pb={4}>
                                            {commsLog.length > 0 ? (
                                                <Table variant="simple">
                                                    <Thead>
                                                        <Tr>
                                                            <Th>Date</Th>
                                                            <Th>Type</Th>
                                                            <Th>Title</Th>
                                                            <Th>Message</Th>
                                                        </Tr>
                                                    </Thead>
                                                    <Tbody>
                                                        {commsLog.map((log) => (
                                                            <Tr key={log.id}>
                                                                <Td>{new Date(log.created_at).toLocaleString()}</Td>
                                                                <Td>{log.comms_type}</Td>
                                                                <Td>{log.msg_title}</Td>
                                                                <Td>{log.msg_body}</Td>
                                                            </Tr>
                                                        ))}
                                                    </Tbody>
                                                </Table>
                                            ) : (
                                                <Text>No communications found</Text>
                                            )}
                                        </AccordionPanel>
                                    </AccordionItem>
                                </Accordion>
                            </Box>
                        </VStack>
                    </GridItem>
                </Grid>
            )}
        </Box>
    );
};

export default ViewUser;
